import { createContext, useContext } from 'react'
import type {
  Access,
  AccessMembership,
  OrganisationRole,
} from '../../data/repositories/accessRepository'

/**
 * The session context and its hook.
 *
 * Split out from SessionProvider so that file exports a component and nothing
 * else. Consumers that only need the type or the hook do not have to import the
 * provider to get them.
 */

export type SessionStatus =
  /** The session has not been resolved yet. Render nothing protected. */
  | 'loading'
  | 'signed-out'
  /** Signed in and access resolved, though possibly with no membership. */
  | 'ready'
  /** Signed in, but access could not be resolved. Distinct from no membership. */
  | 'access-error'

export interface SessionContextValue {
  readonly status: SessionStatus
  readonly userId: string | null
  readonly email: string | null
  readonly fullName: string | null
  readonly access: Access | null
  /** The single active membership, or null. Multi-membership arrives later. */
  readonly membership: AccessMembership | null
  readonly role: OrganisationRole | null
  readonly canViewFinancials: boolean
  /**
   * The employee this login is, or null when nobody has linked one.
   *
   * The same fact `app_private.linked_employee_id` reads, so the screen and the
   * policy cannot disagree about who somebody is. Never derived from an email
   * address, at any layer (Q-38, ADR-109).
   */
  readonly linkedEmployeeId: string | null
  readonly isPlatformAdmin: boolean
  readonly organisationSuspended: boolean
  readonly hasMembership: boolean
  /** Why access resolution failed, for choosing a state page. Never raw. */
  readonly accessFailure: 'denied' | 'incomplete' | 'network' | 'unknown' | null
  signOut: () => Promise<void>
  /** Re-resolves access. Used after a change that could alter membership. */
  refreshAccess: () => Promise<void>
}

export const SessionContext = createContext<SessionContextValue | null>(null)

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext)
  if (value === null) {
    throw new Error('useSession must be used inside a SessionProvider.')
  }
  return value
}
